import { useEffect } from 'react';

/**
 * Runs an async effect with automatic cancellation on cleanup.
 * The async function receives an isCancelled checker - call it after each await
 * to bail out early if the effect was cleaned up (deps changed or unmount).
 * If the effect returns a function, it is used as cleanup (e.g. clearInterval).
 *
 * @example
 * useAsyncEffect(async (isCancelled) => {
 *   const data = await fetchData();
 *   if (isCancelled()) return;
 *   setData(data);
 * }, [someDep]);
 *
 * @example With interval cleanup
 * useAsyncEffect(async (isCancelled) => {
 *   const sync = async () => {
 *     if (isCancelled()) return;
 *     const data = await fetchData();
 *     if (isCancelled()) return;
 *     setData(data);
 *   };
 *   await sync();
 *   const id = setInterval(() => void sync(), 5000);
 *   return () => clearInterval(id);
 * }, [deps]);
 */
export function useAsyncEffect(
  effect: (isCancelled: () => boolean) => Promise<void | (() => void)>,
  deps: React.DependencyList
): void {
  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => void) | void;
    const isCancelled = () => cancelled;

    const run = async () => {
      try {
        const result = await effect(isCancelled);
        if (typeof result === 'function') {
          if (cancelled) {
            result();
          } else {
            cleanup = result;
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[useAsyncEffect] Unhandled error:', error);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, deps);
}
